import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Paper,
  CircularProgress,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import FilterBar from './FIlterBar'
import NewItemModal from './NewItemModal';

const initialFilterState = {
  tags: [],
  attendance: '',
  attendanceOperator: 'gt',
  minPrice: '',
  maxPrice: '',
  sort: '',
  sortDirection: ''
}

const ItemsTable = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTags, setAvailableTags] = useState([]); // State for available tags
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://forteindiedev.com:3081'
  const [initialFilters, setInitialFilters] = useState(initialFilterState)

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchItems = useCallback(
    async (
      tagFilter = [],
      attendanceFilter = '',
      attendanceOperator = 'gt',
      sortField = '',
      minPrice = '', // Added minPrice to parameters
      maxPrice = '', // Added maxPrice to parameters
      sortDir = 'asc' // Added sortDir
    ) => {
      setLoading(true);
      setError(null);
      let url = `${backendUrl}/api/items`;

      const params = new URLSearchParams();
      if (tagFilter.length > 0) { // Changed to check array length
        params.append('tags', tagFilter.join(',')); // Join tags with commas
      }
      if (attendanceFilter) {
        params.append('attendance', attendanceFilter);
        params.append('attendanceOperator', attendanceOperator);
      }
      if (sortField) {
        params.append('sort', sortField);
      }
      if (sortDir) {
        params.append('sortDirection', sortDir);
      }
      if (minPrice) {
        params.append('minPrice', minPrice); // Added to URL params
      }
      if (maxPrice) {
        params.append('maxPrice', maxPrice); // Added to URL params
      }
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch items: ${response.status}`);
        }
        const data = await response.json();
        setItems(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [backendUrl]
  );

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/tags`);
      if (!response.ok) {
          throw new Error(`Failed to fetch tags: ${response.status}`);
      }
      const data = await response.json();
      setAvailableTags(data);
    } catch (error) {
        setError(error.message);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchItems();
    fetchTags();
  }, [fetchItems, fetchTags]);

  // Modal functions
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const doAddItem = async (name, price, tags, type) => {
    try {
      const response = await fetch(`${backendUrl}/api/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          price: Number(price),
          tags: tags.split(',').map(tag => tag.trim()), // Split and trim tags
          type: type,
          last_visited: null, // Or you could have this as a form field
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add item');
      }
  
      const addedItem = await response.json();
      setItems((prevItems) => [...prevItems, addedItem]);
      closeModal(); // Close the modal on success
  
    } catch (error) {
      setError(error.message);
    }
  }

  const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    maxWidth: '800px',
    margin: '0 auto',
    marginTop: theme.spacing(4),
  }));

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: 'left',
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <StyledPaper>
        <Typography color="error">Error: {error}</Typography>
      </StyledPaper>
    );
  }

  if (!items || items.length === 0) {
    return (
      <StyledPaper>
        <Typography>No items to display.</Typography>
      </StyledPaper>
    );
  }

  return (
    <StyledPaper>
      <Typography variant="h4" component="h2" gutterBottom>
        Restaurant Options
      </Typography>
      <FilterBar
        currentFilters={initialFilters}
        setCurrentFilters={setInitialFilters}
        availableTags={availableTags}
        openModal={openModal}
        fetchItems={fetchItems}
      />
      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Last Visit Date</TableCell>
              <TableCell>Min Price</TableCell>
              <TableCell>Tags (Such as Ethnicity)</TableCell>
              <TableCell>Restaurant Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <StyledTableRow key={item.id}>
                <StyledTableCell>{item.name}</StyledTableCell>
                <StyledTableCell>{item.last_visited || 'Never!'}</StyledTableCell>
                <StyledTableCell>${item.price}</StyledTableCell>
                <StyledTableCell>
                  {item.tags && item.tags.length > 0 ? item.tags.join(', ') : 'N/A'}
                </StyledTableCell>
                <StyledTableCell>{item.restaurant_type}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <NewItemModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        doAddItem={doAddItem}
      />
    </StyledPaper>
  );
};

export default ItemsTable;
